import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Trash2 } from 'lucide-react';
import integrationsService from '../services/integrationsService';
import { apiErrorMessage } from '../utils/apiError';
import { dateKey } from '../utils/datetime';
import IntegrationLogTable from './shared/IntegrationLogTable';
import { useAuth } from '../contexts/AuthContext';
import SearchSelect from './shared/SearchSelect';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import BulkStatusResyncModal from './pipelines/BulkStatusResyncModal';
import BulkSelectedResyncModal from './pipelines/BulkSelectedResyncModal';

// Integrate Systems > Logs — every step every integration's pipeline has taken (integration-log-*).
//
// ALL INTEGRATIONS, NOT THE TOP BAR'S. The question this tab answers is usually "what just happened to the
// message I sent", and the integration that handled it is not always the one in the picker. Filters narrow
// it; nothing is hidden by default.
//
// KEPT 14 DAYS (the index's retention). Empty polls are not logged — OXI polls every few seconds; the
// heartbeat on the designer shows that.

const WINDOWS = [
    { value: '1h', label: 'Last hour', ms: 3600e3 },
    { value: '24h', label: 'Last 24 hours', ms: 24 * 3600e3 },
    { value: '7d', label: 'Last 7 days', ms: 7 * 24 * 3600e3 },
    { value: '14d', label: 'Last 14 days', ms: 14 * 24 * 3600e3 },
    // FROM / TO, whole days in the reader's own zone — a day picked is the day they mean, not its UTC slice.
    { value: 'custom', label: 'Between dates…' },
];
const STEPS = [
    { value: '', label: 'All steps' },
    { value: 'oxi.push', label: 'OXI push' },
    { value: 'oxi.poll', label: 'OXI poll' },
    { value: 'outbound', label: 'Outbound (to the Universal API)' },
    { value: 'uapi', label: 'Universal API → OXI' },
    { value: 'test', label: 'Tests from the designer' },
    { value: 'poller', label: 'Event poller' },
];
const OUTCOMES = ['', 'ok', 'warning', 'skipped', 'denied', 'error'];

const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280' };
// A SearchSelect inside a field: the field's caption is upper-case, the choice in it is not.
const control = { textTransform: 'none', letterSpacing: 0, fontWeight: 400, fontSize: '0.85rem', color: '#111827' };
const input = { padding: '0.4rem 0.55rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', color: '#111827', background: '#fff', textTransform: 'none', letterSpacing: 0, fontWeight: 400 };

// The server query for a set of filters. Outside the component: it depends on nothing but its arguments.
const PAGE = 50;
const windowOf = (f) => {
    if (f.window === 'custom') {
        return {
            since: f.from ? new Date(`${f.from}T00:00:00`).toISOString() : undefined,
            until: f.to ? new Date(`${f.to}T23:59:59.999`).toISOString() : undefined,
        };
    }
    return { since: new Date(Date.now() - (WINDOWS.find(w => w.value === f.window) || WINDOWS[1]).ms).toISOString() };
};
const query = (f, offset) => ({
    integrationId: f.integrationId,
    hotelId: f.hotelId,
    system: f.system,
    module: f.module,
    step: f.step,
    outcome: f.outcome,
    q: f.q.trim(),
    transactionId: f.transactionId.trim(),
    ...windowOf(f),
    limit: PAGE,
    offset,
});

// FIXED TO ONE PROPERTY'S PIPELINE — the implementation page passes `integrationId` (its template) and `hotelId`: the
// integration picker is not offered, every query (and a Purge) carries both, and `scopeName` names it in the purge
// confirmation. Without them this is the whole log, as on Pipelines › Logs.
// FIXED TO ONE KIND OF STEP — `step` (a prefix, e.g. 'poller': the template's Events › Processed tab) pins the Step
// filter the same way; each record still opens on its whole transaction.
// ANOTHER SOURCE — `listLogs(params)` / `loadSteps(groupId)` replace the platform-wide log reads (superadmin) with a
// scoped endpoint, as Events › Processed does for everyone who can see the template; `canPurge` / `canResend` withhold
// what only a superadmin may do there.
const IntegrationLogsTab = ({ integrationId: fixedIntegrationId = null, hotelId: fixedHotelId = null, scopeName = null,
    partnerId = null, accountId = null,
    step: fixedStep = null, emptyText = null, listLogs = null, loadSteps = null, canPurge: canPurgeAsked = true, canResend: canResendAsked = true } = {}) => {
    // A hotel's own people read its log too (one hotel, checked on the server); what changes or sends a message —
    // Purge, Resend, Ask AI, Jira — stays the superadmin's.
    const { isSuperAdmin } = useAuth();
    const canPurge = canPurgeAsked && isSuperAdmin;
    const canResend = canResendAsked && isSuperAdmin;
    const list = listLogs || integrationsService.listLogs;
    const fixed = !!fixedIntegrationId;
    // HOTEL, SOURCE AND MODULE offer what the log actually holds (GET /pipelines/logs?facets=1) — the platform-wide log
    // only: a scoped reader (`listLogs`) is not shown the values of hotels it cannot see.
    const withFacets = !listLogs;
    // THE FILTERS CAN ARRIVE IN THE URL, so "open the logs for the test I just ran" is one link rather
    // than an instruction to come here and retype an id. The test modals link in with integrationId and
    // transactionId; anything else on this tab still works the same way.
    //
    // READ ONCE, ON ARRIVAL. They seed the controls and are then the reader's to change — a URL that
    // kept overwriting them would make the filter boxes impossible to clear.
    const [searchParams] = useSearchParams();
    const [integrations, setIntegrations] = useState([]);
    const [filters, setFilters] = useState(() => ({
        integrationId: fixedIntegrationId ? String(fixedIntegrationId) : (searchParams.get('integrationId') || ''),
        hotelId: fixedHotelId ? String(fixedHotelId) : (searchParams.get('hotelId') || ''),
        system: searchParams.get('system') || '',
        module: searchParams.get('module') || '',
        step: fixedStep || searchParams.get('step') || '',
        outcome: searchParams.get('outcome') || '',
        // A LINKED-TO RUN WIDENS THE WINDOW to the widest this tab offers. The default 24h is right for
        // browsing; a link names one exact run, and a run from last week is still the run that was
        // asked for. Not a value outside WINDOWS — `find` falls back to 24h for anything it does not
        // recognise, which would silently narrow the very link this is widening.
        window: searchParams.get('window') || (searchParams.get('transactionId') ? '14d' : '24h'),
        from: searchParams.get('from') || '',
        to: searchParams.get('to') || '',
        q: searchParams.get('q') || '',
        transactionId: searchParams.get('transactionId') || '',
    }));
    const [applied, setApplied] = useState(filters);
    const [groups, setGroups] = useState([]);
    const [next, setNext] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmPurge, setConfirmPurge] = useState(false);
    const [purging, setPurging] = useState(false);
    const [notice, setNotice] = useState(null);
    const [openStatusResync, setOpenStatusResync] = useState(false);
    const [bulkResyncItems, setBulkResyncItems] = useState(null);   // [{ groupId, batch }] when the selection modal is open
    const [facets, setFacets] = useState({ hotels: [], systems: [], modules: [] });

    // THE PIPELINE TEMPLATE FILTER lists only the navbar chain's templates (partner/account), so another chain's
    // template (e.g. Rotana under an Aman context) is not offered. Unscoped only when no chain is in context.
    useEffect(() => {
        if (fixed) return;
        const scope = (partnerId != null || accountId != null) ? { partner_id: partnerId, account_id: accountId } : {};
        integrationsService.getAllIntegrations(scope)
            .then(res => setIntegrations(res.integrations || []))
            .catch(() => setIntegrations([]));
    }, [fixed, partnerId, accountId]);

    const load = useCallback(async (f) => {
        setLoading(true);
        setError(null);
        try {
            const res = await list(query(f, 0));
            setGroups(res.groups || []);
            setNext(res.nextOffset ?? null);
        } catch (e) {
            setError(apiErrorMessage(e, 'The logs could not be loaded.'));
            setGroups([]);
            setNext(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(applied); }, [applied, load]);

    // Re-read with the filters: each list is narrowed by the others (the server leaves out the one it is for).
    useEffect(() => {
        if (!withFacets) return;
        const params = query(applied);
        delete params.limit;
        delete params.offset;
        integrationsService.getLogFacets(params)
            .then(res => setFacets({ hotels: res.hotels || [], systems: res.systems || [], modules: res.modules || [] }))
            .catch(() => {});
    }, [withFacets, applied]);

    const loadMore = async () => {
        setLoading(true);
        try {
            const res = await list(query(applied, next));
            setGroups(g => [...g, ...(res.groups || [])]);
            setNext(res.nextOffset ?? null);
        } catch (e) {
            setError(apiErrorMessage(e, 'More logs could not be loaded.'));
        } finally {
            setLoading(false);
        }
    };

    // WHAT PURGE WILL DELETE, in words — the APPLIED filters (what the table shows), never the half-typed ones.
    const purgeScope = () => {
        const parts = [];
        const integration = integrations.find(i => String(i.id) === String(applied.integrationId));
        if (fixed) parts.push(scopeName || `integration #${applied.integrationId}`);
        else parts.push(applied.integrationId ? `pipeline template "${integration ? integration.name : `#${applied.integrationId}`}"` : 'every pipeline template');
        if (applied.hotelId && !fixedHotelId) {
            const h = facets.hotels.find(x => String(x.id) === String(applied.hotelId));
            parts.push(`hotel ${h ? (h.name || h.property) : `#${applied.hotelId}`}`);
        }
        if (applied.system) parts.push(`source ${applied.system}`);
        if (applied.module) parts.push(`module ${applied.module}`);
        if (applied.step) parts.push(`${(STEPS.find(s => s.value === applied.step) || {}).label || applied.step} steps`);
        if (applied.outcome) parts.push(`outcome ${applied.outcome}`);
        if (applied.transactionId.trim()) parts.push(`transaction ${applied.transactionId.trim()}`);
        if (applied.q.trim()) parts.push(`matching "${applied.q.trim()}"`);
        parts.push(applied.window === 'custom'
            ? `from ${applied.from || 'the start of the log'} to ${applied.to || 'now'}`
            : (WINDOWS.find(w => w.value === applied.window) || WINDOWS[1]).label.toLowerCase());
        return parts.join(', ');
    };

    const purge = async () => {
        setConfirmPurge(false);
        setPurging(true);
        setError(null);
        setNotice(null);
        try {
            const filters = query(applied);
            delete filters.offset;
            delete filters.limit;
            const res = await integrationsService.purgeLogs(filters);
            setNotice(`Purged ${res.deleted} log ${res.deleted === 1 ? 'entry' : 'entries'}.`);
            await load(applied);
        } catch (e) {
            setError(apiErrorMessage(e, 'The logs could not be purged.'));
        } finally {
            setPurging(false);
        }
    };


    const set = (k) => (e) => setFilters(f => ({ ...f, [k]: e.target.value }));
    // Selects apply at once; the two text fields apply on Enter or Search, so a query is not sent per keystroke.
    const setAndApply = (k) => (e) => { const f = { ...filters, [k]: e.target.value }; setFilters(f); setApplied(f); };
    const onEnter = (e) => { if (e.key === 'Enter') setApplied(filters); };
    // BETWEEN DATES opens on the last seven days, so the switch shows something rather than the whole log while the
    // dates are still being picked.
    const chooseWindow = (e) => {
        const w = e.target.value;
        const f = w === 'custom'
            ? { ...filters, window: w, from: filters.from || dateKey(Date.now() - 6 * 24 * 3600e3), to: filters.to || dateKey(Date.now()) }
            : { ...filters, window: w };
        setFilters(f);
        setApplied(f);
    };
    const choose = (k) => (v) => { const f = { ...filters, [k]: v ? String(v) : '' }; setFilters(f); setApplied(f); };

    const hotelItems = facets.hotels.map(h => ({ id: String(h.id), label: h.name || h.property || `Hotel #${h.id}`, code: h.property || undefined }));

    return (
        <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.75rem' }}>
                {!fixed && <div style={{ ...field, width: 230 }}>Pipeline template
                    <div style={control}>
                        <SearchSelect id="ilog-integration" items={integrations.map(i => ({ id: String(i.id), label: i.name }))}
                            value={filters.integrationId || null} onChange={choose('integrationId')}
                            placeholder="All pipeline templates" allowClear clearLabel="All pipeline templates"
                            valueLabel={filters.integrationId ? `#${filters.integrationId}` : null} />
                    </div>
                </div>}
                {!fixedHotelId && withFacets && <div style={{ ...field, width: 230 }}>Hotel
                    <div style={control}>
                        <SearchSelect id="ilog-hotel" items={hotelItems}
                            value={filters.hotelId || null} onChange={choose('hotelId')}
                            placeholder="All hotels" allowClear clearLabel="All hotels"
                            valueLabel={filters.hotelId ? `Hotel #${filters.hotelId}` : null}
                            emptyLabel="No hotel has logged anything for these filters." />
                    </div>
                </div>}
                {withFacets && <label style={field} htmlFor="ilog-system">Source
                    <select id="ilog-system" style={input} value={filters.system} onChange={setAndApply('system')}>
                        <option value="">All sources</option>
                        {[...new Set([...facets.systems, filters.system].filter(Boolean))].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>}
                {withFacets && <label style={field} htmlFor="ilog-module">Module
                    <select id="ilog-module" style={input} value={filters.module} onChange={setAndApply('module')}>
                        <option value="">All modules</option>
                        {[...new Set([...facets.modules, filters.module].filter(Boolean))].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>}
                {!fixedStep && <label style={field} htmlFor="ilog-step">Step
                    <select id="ilog-step" style={input} value={filters.step} onChange={setAndApply('step')}>
                        {STEPS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </label>}
                <label style={field} htmlFor="ilog-outcome">Outcome
                    <select id="ilog-outcome" style={input} value={filters.outcome} onChange={setAndApply('outcome')}>
                        {OUTCOMES.map(o => <option key={o} value={o}>{o || 'Any'}</option>)}
                    </select>
                </label>
                <label style={field} htmlFor="ilog-window">When
                    <select id="ilog-window" style={input} value={filters.window} onChange={chooseWindow}>
                        {WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select>
                </label>
                {/* Applied once both ends are set (or on Enter / Refresh), not on the first date picked. */}
                {filters.window === 'custom' && <>
                    <label style={field} htmlFor="ilog-from">From
                        <input id="ilog-from" type="date" style={input} value={filters.from} max={filters.to || undefined}
                            onChange={(e) => { const f = { ...filters, from: e.target.value }; setFilters(f); if (f.from && f.to) setApplied(f); }} onKeyDown={onEnter} />
                    </label>
                    <label style={field} htmlFor="ilog-to">To
                        <input id="ilog-to" type="date" style={input} value={filters.to} min={filters.from || undefined}
                            onChange={(e) => { const f = { ...filters, to: e.target.value }; setFilters(f); if (f.from && f.to) setApplied(f); }} onKeyDown={onEnter} />
                    </label>
                </>}
                <label style={field} htmlFor="ilog-txn">Transaction
                    <input id="ilog-txn" style={{ ...input, width: 200 }} value={filters.transactionId} onChange={set('transactionId')} onKeyDown={onEnter} placeholder="e.g. HCONN-TEST-…" />
                </label>
                <label style={{ ...field, flex: 1, minWidth: 200 }} htmlFor="ilog-q">Search
                    <input id="ilog-q" style={input} value={filters.q} onChange={set('q')} onKeyDown={onEnter}
                        placeholder='Anything in the message — a confirmation number, a guest name, an error ("quotes" for a phrase)' />
                </label>
                <button type="button" className="btn btn-secondary" onClick={() => setApplied({ ...filters })} disabled={loading}>
                    <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} /> Refresh
                </button>
                {/* Purges what the filters select — the same query the table ran — after saying so in words. */}
                {canPurge && <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setConfirmPurge(true)}
                    disabled={loading || purging || !groups.length}
                    title={groups.length ? 'Delete the log entries these filters select' : 'Nothing to purge for these filters'}
                    style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                >
                    <Trash2 size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} /> {purging ? 'Purging…' : 'Purge'}
                </button>}
                {/* Resync every record of the chosen status across all hotels — one run per implementation + kind,
                    polled in a modal. Superadmin only (the log itself is). */}
                {canPurge && <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setOpenStatusResync(true); setError(null); }}
                    disabled={loading}
                    title="Re-query OHIP for every record of the chosen status, across every hotel"
                    style={{ color: '#1e40af', borderColor: '#c7d2fe', marginLeft: '0.5rem' }}
                >
                    <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} /> Resync by status
                </button>}
            </div>

            {notice && (
                <div role="status" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '0.6rem 1rem', fontSize: '0.9rem' }}>{notice}</div>
            )}

            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem' }}>{error}</div>
            )}

            {loading && !groups.length ? <Spinner /> : (
                <IntegrationLogTable
                    groups={groups}
                    // ONE INTEGRATION'S LOG names it in every row otherwise — the page already says which.
                    showIntegration={!fixed}
                    emptyText={emptyText || 'Nothing logged for these filters. Logs are kept for 14 days; empty OXI polls are not logged.'}
                    canResend={canResend}
                    canAssist={isSuperAdmin}
                    listLogs={list}
                    // Select failed rows across hotels and resync them together — superadmin only, same gate as per-row resync.
                    selectable={canResend}
                    onBulkResync={setBulkResyncItems}
                    {...(loadSteps ? { loadSteps } : {})}
                />
            )}

            {confirmPurge && (
                <ConfirmModal
                    title="Purge integration logs?"
                    message={`This permanently deletes every log entry for ${purgeScope()} — not just the ${groups.length} records loaded here. It cannot be undone.`}
                    confirmLabel="Purge"
                    danger
                    onConfirm={purge}
                    onCancel={() => setConfirmPurge(false)}
                />
            )}

            {openStatusResync && (
                <BulkStatusResyncModal onClose={() => setOpenStatusResync(false)} />
            )}
            {bulkResyncItems && (
                <BulkSelectedResyncModal items={bulkResyncItems} onClose={() => setBulkResyncItems(null)} />
            )}

            {next !== null && groups.length > 0 && (
                <div style={{ textAlign: 'center' }}>
                    <button type="button" className="btn btn-secondary" onClick={loadMore} disabled={loading}>
                        {loading ? 'Loading…' : 'Load older'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default IntegrationLogsTab;
